import { useState, useRef } from "react";
import { useLocation } from "wouter";
import { useInstallerApply } from "@/api/hooks";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { useAuth } from "@/_core/hooks/useAuth";
import { LoginPrompt } from "@/components/auth/AuthDialog";
import HelioLogoMark, { HelioHomeLink } from "@/components/HelioLogoMark";
import HeaderAuth from "@/components/HeaderAuth";
import { TRADING_NAME } from "@/constants/company";
import {
  CheckCircle, Upload, ArrowRight, ArrowLeft, Sun, Shield,
  Building2, FileText, MapPin, Users, Loader2
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

// ─── Types ────────────────────────────────────────────────────────────────────
interface CertFile {
  file: File | null;
  base64: string | null;
  name: string | null;
}

interface FormState {
  companyName: string;
  mcsNumber: string;
  reccNumber: string;
  coveragePostcodes: string;
  yearsTrading: number;
  teamSize: number;
  website: string;
  bio: string;
  mcsCert: CertFile;
  reccCert: CertFile;
  insuranceCert: CertFile;
}

const STEPS = ["Company", "Certifications", "Coverage", "Review"];

function StepIndicator({ current }: { current: number }) {
  return (
    <div className="flex items-center gap-2">
      {STEPS.map((label, i) => (
        <div key={label} className="flex items-center gap-2">
          <div className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-semibold transition-all ${
            i < current ? "bg-brand-lime text-[#050d1a]" :
            i === current ? "bg-brand-lime/20 border border-brand-lime text-brand-lime" :
            "bg-white/8 text-white/30"
          }`}>
            {i < current ? <CheckCircle className="h-3.5 w-3.5" /> : i + 1}
          </div>
          <span className={`hidden sm:block text-xs ${i === current ? "text-white" : "text-white/30"}`}>{label}</span>
          {i < STEPS.length - 1 && <div className={`h-px w-6 ${i < current ? "bg-brand-lime" : "bg-white/10"}`} />}
        </div>
      ))}
    </div>
  );
}

function CertUpload({ label, cert, onChange }: {
  label: string;
  cert: CertFile;
  onChange: (c: CertFile) => void;
}) {
  const ref = useRef<HTMLInputElement>(null);
  const handleFile = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target?.result as string;
      // strip data:...;base64, prefix
      const base64 = dataUrl.split(",")[1] ?? null;
      onChange({ file, base64, name: file.name });
    };
    reader.readAsDataURL(file);
  };
  return (
    <div>
      <Label className="text-white/70 text-sm mb-2 block">{label}</Label>
      <div
        onClick={() => ref.current?.click()}
        className={`cursor-pointer rounded-xl border-2 border-dashed p-6 text-center transition-all ${
          cert.file ? "border-brand-lime/60 bg-brand-lime/5" : "border-white/20 bg-[#0d1b2e] hover:border-brand-lime/40"
        }`}
      >
        <input
          ref={ref}
          type="file"
          accept=".pdf,.jpg,.jpeg,.png"
          className="hidden"
          onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])}
        />
        {cert.file ? (
          <div className="flex items-center justify-center gap-2">
            <CheckCircle className="h-5 w-5 text-brand-lime" />
            <span className="text-sm text-brand-lime font-medium">{cert.name}</span>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2">
            <Upload className="h-6 w-6 text-white/30" />
            <p className="text-sm text-white/40">Click to upload · PDF, JPG, PNG</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function InstallerApply() {
  const [, navigate] = useLocation();
  const { user, loading, isAuthenticated } = useAuth();
  const [step, setStep] = useState(0);
  const [submitted, setSubmitted] = useState(false);

  const [form, setForm] = useState<FormState>({
    companyName: "",
    mcsNumber: "",
    reccNumber: "",
    coveragePostcodes: "",
    yearsTrading: 1,
    teamSize: 2,
    website: "",
    bio: "",
    mcsCert: { file: null, base64: null, name: null },
    reccCert: { file: null, base64: null, name: null },
    insuranceCert: { file: null, base64: null, name: null },
  });

  const apply = useInstallerApply();

  const update = (patch: Partial<FormState>) => setForm((f) => ({ ...f, ...patch }));

  const handleSubmit = async () => {
    try {
      await apply.mutateAsync({
        companyName: form.companyName,
        mcsNumber: form.mcsNumber,
        reccNumber: form.reccNumber || undefined,
        coveragePostcodes: form.coveragePostcodes,
        yearsTrading: form.yearsTrading,
        teamSize: form.teamSize,
        website: form.website || undefined,
        bio: form.bio || undefined,
        mcsCertBase64: form.mcsCert.base64 ?? undefined,
        mcsCertMime: form.mcsCert.file?.type ?? undefined,
        mcsCertName: form.mcsCert.name ?? undefined,
        reccCertBase64: form.reccCert.base64 ?? undefined,
        reccCertMime: form.reccCert.file?.type ?? undefined,
        reccCertName: form.reccCert.name ?? undefined,
        insuranceCertBase64: form.insuranceCert.base64 ?? undefined,
        insuranceCertMime: form.insuranceCert.file?.type ?? undefined,
        insuranceCertName: form.insuranceCert.name ?? undefined,
      });
      setSubmitted(true);
    } catch {
      toast.error("Failed to submit application. Please try again.");
    }
  };

  const slideVariants = {
    enter: { opacity: 0, x: 40 },
    center: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: -40 },
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050d1a] flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-brand-lime animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#050d1a] text-white">
        <LoginPrompt message={`You need a ${TRADING_NAME} account to submit an installer application.`} />
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#050d1a] text-white flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center max-w-md"
        >
          <div className="flex h-20 w-20 items-center justify-center rounded-full bg-brand-lime/10 border border-brand-lime/30 mx-auto mb-6">
            <CheckCircle className="h-10 w-10 text-brand-lime" />
          </div>
          <h1 className="font-display text-3xl font-bold mb-3">Application submitted</h1>
          <p className="text-white/50 mb-2">Thank you, {user?.name?.split(" ")[0]}. We've received your application for <span className="text-white font-medium">{form.companyName}</span>.</p>
          <p className="text-white/40 text-sm mb-8">Our team will review your certifications and coverage area. You'll receive a notification once your application has been reviewed — typically within 2 business days.</p>
          <div className="rounded-xl border border-white/10 bg-[#0d1b2e] p-4 text-left mb-6">
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-white/40">MCS Number</span><span className="text-white">{form.mcsNumber}</span></div>
              <div className="flex justify-between"><span className="text-white/40">Coverage</span><span className="text-white text-right max-w-[60%]">{form.coveragePostcodes}</span></div>
              <div className="flex justify-between"><span className="text-white/40">Team size</span><span className="text-white">{form.teamSize} engineers</span></div>
            </div>
          </div>
          <Button variant="outline" className="border-white/20 text-white hover:bg-white/5" onClick={() => navigate("/")}>
            Back to home
          </Button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050d1a] text-white">
      {/* Header */}
      <div className="border-b border-white/8 bg-[#050d1a]/80 backdrop-blur-xl sticky top-0 z-40">
        <div className="container flex h-16 items-center justify-between">
          <HelioHomeLink className="flex items-center gap-2">
            <HelioLogoMark linked={false} className="text-brand-lime" />
          </HelioHomeLink>
          <StepIndicator current={step} />
          <HeaderAuth />
        </div>
      </div>

      <div className="container py-12 max-w-2xl">
        <AnimatePresence mode="wait">
          {/* ── Step 0: Company Details ── */}
          {step === 0 && (
            <motion.div key="step0" variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.3, ease: [0.23, 1, 0.32, 1] }}>
              <div className="text-center mb-10">
                <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-lime/10 border border-brand-lime/30 mx-auto mb-4">
                  <Building2 className="h-7 w-7 text-brand-lime" />
                </div>
                <h1 className="font-display text-3xl font-bold mb-3">Join the {TRADING_NAME} installer network</h1>
                <p className="text-white/50">Tell us about your company. MCS-certified installers only.</p>
              </div>
              <div className="space-y-5">
                <div>
                  <Label className="text-white/70 text-sm mb-2 block">Company name *</Label>
                  <Input
                    value={form.companyName}
                    onChange={(e) => update({ companyName: e.target.value })}
                    placeholder="e.g. Bright Solar Solutions Ltd"
                    className="bg-[#0d1b2e] border-white/15 text-white placeholder:text-white/30 h-12"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-white/70 text-sm mb-2 block">Years trading *</Label>
                    <Input
                      type="number"
                      min={0}
                      value={form.yearsTrading}
                      onChange={(e) => update({ yearsTrading: parseInt(e.target.value) || 0 })}
                      className="bg-[#0d1b2e] border-white/15 text-white h-12"
                    />
                  </div>
                  <div>
                    <Label className="text-white/70 text-sm mb-2 block">Team size (engineers) *</Label>
                    <Input
                      type="number"
                      min={1}
                      value={form.teamSize}
                      onChange={(e) => update({ teamSize: parseInt(e.target.value) || 1 })}
                      className="bg-[#0d1b2e] border-white/15 text-white h-12"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-white/70 text-sm mb-2 block">Website (optional)</Label>
                  <Input
                    value={form.website}
                    onChange={(e) => update({ website: e.target.value })}
                    placeholder="https://yoursolarcompany.co.uk"
                    className="bg-[#0d1b2e] border-white/15 text-white placeholder:text-white/30 h-12"
                  />
                </div>
                <div>
                  <Label className="text-white/70 text-sm mb-2 block">Company bio (optional)</Label>
                  <Textarea
                    value={form.bio}
                    onChange={(e) => update({ bio: e.target.value })}
                    placeholder="Brief description of your company, specialisms, and experience..."
                    className="bg-[#0d1b2e] border-white/15 text-white placeholder:text-white/30 min-h-[100px]"
                  />
                </div>
              </div>
              <div className="mt-8">
                <Button
                  className="btn-cta w-full"
                  disabled={!form.companyName || form.teamSize < 1}
                  onClick={() => setStep(1)}
                >
                  Continue <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </motion.div>
          )}

          {/* ── Step 1: Certifications ── */}
          {step === 1 && (
            <motion.div key="step1" variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.3, ease: [0.23, 1, 0.32, 1] }}>
              <div className="text-center mb-10">
                <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-lime/10 border border-brand-lime/30 mx-auto mb-4">
                  <Shield className="h-7 w-7 text-brand-lime" />
                </div>
                <h1 className="font-display text-3xl font-bold mb-3">Certifications</h1>
                <p className="text-white/50">Your MCS number is required. Upload certificates for faster approval.</p>
              </div>
              <div className="space-y-5">
                <div>
                  <Label className="text-white/70 text-sm mb-2 block">MCS number *</Label>
                  <Input
                    value={form.mcsNumber}
                    onChange={(e) => update({ mcsNumber: e.target.value })}
                    placeholder="e.g. MCS-12345"
                    className="bg-[#0d1b2e] border-white/15 text-white placeholder:text-white/30 h-12"
                  />
                </div>
                <div>
                  <Label className="text-white/70 text-sm mb-2 block">RECC number (optional)</Label>
                  <Input
                    value={form.reccNumber}
                    onChange={(e) => update({ reccNumber: e.target.value })}
                    placeholder="e.g. RECC-67890"
                    className="bg-[#0d1b2e] border-white/15 text-white placeholder:text-white/30 h-12"
                  />
                </div>
                <CertUpload
                  label="MCS certificate *"
                  cert={form.mcsCert}
                  onChange={(c) => update({ mcsCert: c })}
                />
                <CertUpload
                  label="RECC certificate (optional)"
                  cert={form.reccCert}
                  onChange={(c) => update({ reccCert: c })}
                />
                <CertUpload
                  label="Public liability insurance (optional)"
                  cert={form.insuranceCert}
                  onChange={(c) => update({ insuranceCert: c })}
                />
              </div>
              <div className="mt-8 flex gap-3">
                <Button variant="outline" className="border-white/20 text-white hover:bg-white/5" onClick={() => setStep(0)}>
                  <ArrowLeft className="mr-2 h-4 w-4" /> Back
                </Button>
                <Button
                  className="btn-cta flex-1"
                  disabled={!form.mcsNumber}
                  onClick={() => setStep(2)}
                >
                  Continue <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </motion.div>
          )}

          {/* ── Step 2: Coverage Area ── */}
          {step === 2 && (
            <motion.div key="step2" variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.3, ease: [0.23, 1, 0.32, 1] }}>
              <div className="text-center mb-10">
                <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-lime/10 border border-brand-lime/30 mx-auto mb-4">
                  <MapPin className="h-7 w-7 text-brand-lime" />
                </div>
                <h1 className="font-display text-3xl font-bold mb-3">Coverage area</h1>
                <p className="text-white/50">Enter the postcode prefixes or areas you cover. We use this to match you to nearby leads.</p>
              </div>
              <div className="space-y-5">
                <div>
                  <Label className="text-white/70 text-sm mb-2 block">Coverage postcodes / areas *</Label>
                  <Textarea
                    value={form.coveragePostcodes}
                    onChange={(e) => update({ coveragePostcodes: e.target.value })}
                    placeholder="e.g. SW, SE, W, EC, WC, N, NW — or list specific postcodes"
                    className="bg-[#0d1b2e] border-white/15 text-white placeholder:text-white/30 min-h-[120px]"
                  />
                  <p className="text-white/30 text-xs mt-2">Separate areas with commas. You can update this at any time from your installer dashboard.</p>
                </div>
                <div className="rounded-xl border border-white/10 bg-[#0d1b2e] p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Users className="h-4 w-4 text-brand-lime" />
                    <span className="text-sm font-medium text-white">How lead matching works</span>
                  </div>
                  <p className="text-white/40 text-sm">When a customer submits a quote in your coverage area, you'll receive a lead notification. You can accept or decline each lead from your installer dashboard.</p>
                </div>
              </div>
              <div className="mt-8 flex gap-3">
                <Button variant="outline" className="border-white/20 text-white hover:bg-white/5" onClick={() => setStep(1)}>
                  <ArrowLeft className="mr-2 h-4 w-4" /> Back
                </Button>
                <Button
                  className="btn-cta flex-1"
                  disabled={!form.coveragePostcodes.trim()}
                  onClick={() => setStep(3)}
                >
                  Review application <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </motion.div>
          )}

          {/* ── Step 3: Review & Submit ── */}
          {step === 3 && (
            <motion.div key="step3" variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.3, ease: [0.23, 1, 0.32, 1] }}>
              <div className="text-center mb-10">
                <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-lime/10 border border-brand-lime/30 mx-auto mb-4">
                  <FileText className="h-7 w-7 text-brand-lime" />
                </div>
                <h1 className="font-display text-3xl font-bold mb-3">Review your application</h1>
                <p className="text-white/50">Check your details before submitting. Our team will review within 2 business days.</p>
              </div>
              <div className="space-y-4">
                <div className="rounded-xl border border-white/10 bg-[#0d1b2e] p-5">
                  <h3 className="text-sm font-semibold text-white/60 uppercase tracking-wider mb-3">Company</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between"><span className="text-white/40">Company name</span><span className="text-white font-medium">{form.companyName}</span></div>
                    <div className="flex justify-between"><span className="text-white/40">Years trading</span><span className="text-white">{form.yearsTrading} year{form.yearsTrading !== 1 ? "s" : ""}</span></div>
                    <div className="flex justify-between"><span className="text-white/40">Team size</span><span className="text-white">{form.teamSize} engineer{form.teamSize !== 1 ? "s" : ""}</span></div>
                    {form.website && <div className="flex justify-between"><span className="text-white/40">Website</span><span className="text-white">{form.website}</span></div>}
                  </div>
                </div>
                <div className="rounded-xl border border-white/10 bg-[#0d1b2e] p-5">
                  <h3 className="text-sm font-semibold text-white/60 uppercase tracking-wider mb-3">Certifications</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between"><span className="text-white/40">MCS number</span><span className="text-white font-medium">{form.mcsNumber}</span></div>
                    {form.reccNumber && <div className="flex justify-between"><span className="text-white/40">RECC number</span><span className="text-white">{form.reccNumber}</span></div>}
                    <div className="flex justify-between"><span className="text-white/40">MCS certificate</span><span className={form.mcsCert.file ? "text-brand-lime" : "text-white/30"}>{form.mcsCert.file ? "Uploaded" : "Not uploaded"}</span></div>
                    <div className="flex justify-between"><span className="text-white/40">Insurance</span><span className={form.insuranceCert.file ? "text-brand-lime" : "text-white/30"}>{form.insuranceCert.file ? "Uploaded" : "Not uploaded"}</span></div>
                  </div>
                </div>
                <div className="rounded-xl border border-white/10 bg-[#0d1b2e] p-5">
                  <h3 className="text-sm font-semibold text-white/60 uppercase tracking-wider mb-3">Coverage</h3>
                  <p className="text-sm text-white">{form.coveragePostcodes}</p>
                </div>
              </div>
              <div className="mt-8 flex gap-3">
                <Button variant="outline" className="border-white/20 text-white hover:bg-white/5" onClick={() => setStep(2)}>
                  <ArrowLeft className="mr-2 h-4 w-4" /> Back
                </Button>
                <Button
                  className="btn-cta flex-1"
                  onClick={handleSubmit}
                  disabled={apply.isPending}
                >
                  {apply.isPending ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Submitting...</>
                  ) : (
                    <>Submit application <ArrowRight className="ml-2 h-4 w-4" /></>
                  )}
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
